// TODO - Type 'any' needs to be fixed.
import { compareAsc, differenceInHours } from "date-fns";
import format from "date-fns/format";
import { useEffect, useState } from "react";

import {JOB, SKILLS} from "../../../../../../assets/constants";
import useSchedulerData from "../../../../controllers/use-scheduler-data";
import {Shift, SkillInShift} from "../types";
import {getColumns, getColumnsBySkills} from "../utils";

interface UseViewSummaryProps {
    allWeekShifts: Shift[];
	allWeekShiftsBySkills: Shift[];
	selectedShiftIds: Set<number>;
	onShiftToggle: (shiftId: number) => void;
}

const useViewSummary = ({
	allWeekShifts,
	allWeekShiftsBySkills,
	selectedShiftIds,
	onShiftToggle,
}: UseViewSummaryProps) => {
	const { minDurationHrs, maxDurationHrs, filterAllLocations, filterJobRoles, shiftsByFilter } = useSchedulerData();
	const [rows, setRows] = useState<any>([]);
	const [selectedColumn, setSelectedColumns] = useState<any>(
		getColumns(selectedShiftIds, onShiftToggle)
	);

	useEffect(() => {
		const rowData = [];
		const getPropsForRow = (shift: any) => {
			const time = {
				start: `${format(new Date(shift?.startDateTime), "h:mm a")}`,
				end: `${format(new Date(shift?.endDateTime), "h:mm a")}`,
			};
			const hrsDifference = differenceInHours(
				new Date(shift?.endDateTime),
				new Date(shift?.startDateTime)
			);
			const shiftDateTime = shift?.startDateTime;
			const shiftId = shift?.id;
			const selectedLocation = filterAllLocations?.find(
				(location: any) => location.id === shift?.locationId
			);
			const effectiveMinDurationHrs =
				selectedLocation?.minShiftDuration ?? minDurationHrs;
			const effectiveMaxDurationHrs =
				selectedLocation?.maxShiftDuration ?? maxDurationHrs;
			const isValid =
				hrsDifference >= effectiveMinDurationHrs &&
				hrsDifference <= effectiveMaxDurationHrs;

			return {
				time,
				shiftDateTime,
				shiftId,
				isValid,
				workcenter: shift?.workcenter,
				locationName: shift?.locationName,
			};
		};

		if (shiftsByFilter === JOB) {
			if (allWeekShifts) {
				const jobRoleShiftMapping: any = {};
				allWeekShifts.forEach((shift) => {
					const jobRole = (shift as any).jobName;
					const props = getPropsForRow(shift);

					if (!Object.hasOwn(jobRoleShiftMapping, jobRole)) {
						jobRoleShiftMapping[jobRole] = [];
					}
					jobRoleShiftMapping[jobRole].push(props);
				});

				const jobRoles = Object.keys(jobRoleShiftMapping);

				for (let i = 0; i < jobRoles.length; i++) {
					const jobRole = jobRoles[i];
					const currentJobRole = filterJobRoles.filter(
						({ name }: { name: string }) => name === jobRole
					)[0];
					rowData.push({
						jobRole: currentJobRole,
						shiftDetails: jobRoleShiftMapping[jobRole],
						id: jobRole?.trim(),
					});
				}
			}
			setSelectedColumns(getColumns(selectedShiftIds, onShiftToggle));
		} else if (shiftsByFilter === SKILLS) {
			const skillsShiftMapping: any = {};
			allWeekShiftsBySkills.forEach((shift) => {
				const skillsUniqueLabelArray = shift.skills.map(
					(skill: SkillInShift) => skill.skillId
				);
				const skillsUniqueLabel = skillsUniqueLabelArray.sort().join(",");
				const props = getPropsForRow(shift);

				if (!Object.hasOwn(skillsShiftMapping, skillsUniqueLabel)) {
					skillsShiftMapping[skillsUniqueLabel] = [];
				}
				skillsShiftMapping[skillsUniqueLabel].push({
					...props,
					skills: shift.skills,
				});
			});

			const skills = Object.keys(skillsShiftMapping);

			for (let i = 0; i < skills.length; i++) {
				const rowSkills = skills[i];
				rowData.push({
					skillName: skillsShiftMapping[rowSkills][0]?.skills
						.map((skill: { skillName: any }) => skill?.skillName)
						.sort()
						.join(", "),
					skills: skillsShiftMapping[rowSkills][0].skills,
					shiftDetails: skillsShiftMapping[rowSkills],
					id: rowSkills,
				});
			}
			setSelectedColumns(getColumnsBySkills(selectedShiftIds, onShiftToggle));
		}

		const sortRows = rowData.map((row) => {
			const { shiftDetails } = row;
			const sortedDates = [...shiftDetails].sort((date1, date2) => {
				const startOfWeekDate1 = new Date(date1.shiftDateTime);
				const startOfWeekDate2 = new Date(date2.shiftDateTime);
				return compareAsc(startOfWeekDate1, startOfWeekDate2);
			});
			return { ...row, shiftSortedDetails: sortedDates };
		});
		setRows(sortRows);
	}, [
		allWeekShiftsBySkills,
		filterJobRoles,
		filterAllLocations,
		allWeekShifts,
		maxDurationHrs,
		minDurationHrs,
		shiftsByFilter,
		selectedShiftIds,
		onShiftToggle,
	]);

	return [{ minDurationHrs, maxDurationHrs, rows, selectedColumn }, { setRows }];
};

export default useViewSummary;
